import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BRANDING_BY_MODE,
  COMMUNITY_COORDINATES,
  CONTACT,
  FIREBASE_HINTS,
  HERO_IMAGE,
} from './branding.config.js';
import { branding } from './siteConfig.js';

const SECTIONS = [
  { id: 'overview', title: 'System Overview' },
  { id: 'stack', title: 'Technology Stack' },
  { id: 'frontend', title: 'Frontend Architecture' },
  { id: 'provisioning', title: 'New HOA Customer Provisioning' },
  { id: 'firebase', title: 'Firebase Configuration' },
  { id: 'functions', title: 'Cloud Functions' },
  { id: 'firestore', title: 'Firestore Data Model' },
  { id: 'storage', title: 'Cloud Storage' },
  { id: 'auth', title: 'Authentication & Admin Claims' },
  { id: 'gate', title: 'Gate Access System' },
  { id: 'mailbox', title: 'Mailbox & Ring Integration' },
  { id: 'vehicles', title: 'Vehicle Log Pipeline' },
  { id: 'identification', title: 'Identification APIs' },
  { id: 'notifications', title: 'Notifications' },
  { id: 'payments', title: 'Stripe Payments' },
  { id: 'hoa-finance', title: 'HOA Finance (AP)' },
  { id: 'hoa-compliance', title: 'Governance & Compliance' },
  { id: 'hoa-operations', title: 'Community Operations' },
  { id: 'environmental', title: 'Wildfire & Air Quality' },
  { id: 'mobile', title: 'Mobile App' },
  { id: 'marketing', title: 'Marketing & Product Catalog' },
  { id: 'parcel-gis', title: 'Parcel GIS (Tax Assessor)' },
  { id: 'smart-integrations', title: 'Smart-Home Integrations' },
  { id: 'deploy', title: 'Deployment & Operations' },
  { id: 'local', title: 'Local Development' },
];

function DeveloperGuidePage({ user, isAdmin, isMobile }) {
  useEffect(() => {
    document.title = 'Developer Guide | Nantahala Mountain Resort';
    return () => {
      document.title = 'NMR Community';
    };
  }, []);

  if (!isAdmin) {
    return (
      <div className={`docs-page ${isMobile ? 'docs-page-mobile' : ''}`}>
        <article className="docs-card">
          <h1>Access denied</h1>
          <p>The developer guide is restricted to administrators.</p>
          <Link to="/admin" className="docs-link">Return to Admin Portal</Link>
        </article>
      </div>
    );
  }

  return (
    <div className={`docs-page ${isMobile ? 'docs-page-mobile' : ''}`}>
      <article className="docs-card docs-card-wide docs-card-dev">
        <header className="docs-header">
          <h1 className="docs-title">Developer Guide</h1>
          <p className="docs-meta">
            Nantahala Mountain Resort Community Portal — technical reference
            <br />
            Signed in as {user?.email}
          </p>
        </header>

        <nav className="docs-toc" aria-label="Developer guide sections">
          <h2 className="docs-toc-title">Contents</h2>
          <ul>
            {SECTIONS.map((section) => (
              <li key={section.id}>
                <a href={`#${section.id}`}>{section.title}</a>
              </li>
            ))}
          </ul>
        </nav>

        <section id="overview" className="docs-section">
          <h2>System Overview</h2>
          <p>
            NMR is a Firebase-backed React SPA with a companion Expo mobile app. Owner-facing features run in the
            browser at <code>nantahalamountainresort.org</code> (Firebase Hosting). Business logic, integrations, and
            privileged operations live in Cloud Functions (Node.js 22, <code>us-central1</code>). Persistent state
            uses the named Firestore database <code>nmrdb</code>, Cloud Storage, and Firebase Auth custom claims.
          </p>
          <pre className="docs-code-block">{`Browser / Mobile App
        │
        ▼
Firebase Auth (email/password, isAdmin claim)
        │
        ├── Firestore (nmrdb) ── owners, polls, gate_plates, vehicle_*, mailbox_*
        ├── Cloud Storage ── photos, vehicle thumbnails, HOA files
        └── Cloud Functions ── Ring, gate LPR, Stripe, vision AI, news/events`}</pre>
        </section>

        <section id="stack" className="docs-section">
          <h2>Technology Stack</h2>
          <ul>
            <li><strong>Web</strong> — React 18, React Router, CRA + CRACO, Chart.js, Firebase JS SDK v9+</li>
            <li><strong>Functions</strong> — firebase-functions v2 (onCall, onRequest, onSchedule), firebase-admin</li>
            <li><strong>Mobile</strong> — Expo Router, React Native, shared Firebase project</li>
            <li><strong>Payments</strong> — Stripe (SetupIntent, PaymentMethod, invoice pay)</li>
            <li><strong>Vision</strong> — xAI Grok vision for mailbox, vehicle, and species identification</li>
            <li><strong>Hardware</strong> — Ring API (mailbox cameras), gate LPR webhook</li>
            <li><strong>External data</strong> — USGS seismic, NCDOT roads, FlightRadar24, RSS news, curated events</li>
          </ul>
        </section>

        <section id="provisioning" className="docs-section">
          <h2>New HOA Customer Provisioning</h2>
          <p>
            Use <code>src/branding.config.js</code> as the single source for site name, contact emails,
            map coordinates, and Firebase project hints. Update that file (or fork per customer) before
            building and deploying.
          </p>
          <h3>Checklist</h3>
          <ol className="docs-checklist">
            <li>
              <strong>Firebase project</strong> — Create or select a project; set{' '}
              <code>projectId</code>, <code>authDomain</code>, <code>storageBucket</code>, and named
              Firestore DB <code>{FIREBASE_HINTS.firestoreDatabaseId}</code> in{' '}
              <code>branding.config.js</code> and <code>src/firebase.js</code>. Run{' '}
              <code>firebase target:apply hosting main &lt;site-id&gt;</code> and{' '}
              <code>firebase target:apply hosting demo &lt;demo-site-id&gt;</code> for dual hosting.
            </li>
            <li>
              <strong>Secrets</strong> — Set Cloud Functions secrets: GMAIL_USER, GMAIL_PASS, XAI_API_KEY,
              STRIPE_SECRET, RAPIDAPI_KEY, FR24_TOKEN, WEATHER_API_KEY, plus Ring OAuth vars for mailbox.
              Verify with <code>firebase functions:secrets:access</code> before first deploy.
            </li>
            <li>
              <strong>Hosting targets</strong> — Confirm <code>firebase.json</code> targets{' '}
              <code>main</code> → <code>{FIREBASE_HINTS.hostingTargets.main.public}/</code> and{' '}
              <code>demo</code> → <code>{FIREBASE_HINTS.hostingTargets.demo.public}/</code>. Production:{' '}
              <code>npm run build && firebase deploy --only hosting:main</code>.
            </li>
            <li>
              <strong>Demo build</strong> — <code>npm run build:demo</code> (sets{' '}
              <code>REACT_APP_DEMO_MODE=true</code> and mocks Firebase). Deploy with{' '}
              <code>npm run deploy:demo</code>. Marketing landing: <code>npm run build:marketing</code>.
            </li>
            <li>
              <strong>Hero image</strong> — Default asset <code>{HERO_IMAGE.defaultAsset}</code> is optimized (~270 KB);
              compress to under 500 KB (WebP or optimized JPEG) before customer launch. Optional override:{' '}
              <code>{HERO_IMAGE.envVar}</code> in <code>.env.production</code>.
            </li>
            <li>
              <strong>Branding fields</strong> — Set <code>siteName</code>, <code>communityName</code>,{' '}
              <code>contactEmail</code> ({CONTACT.contactEmail}), <code>salesEmail</code> ({CONTACT.salesEmail}),
              and coordinates ({COMMUNITY_COORDINATES.latitude}, {COMMUNITY_COORDINATES.longitude}) in{' '}
              <code>BRANDING_BY_MODE.production</code>.
            </li>
            <li>
              <strong>Mobile rebuild</strong> — Update <code>mobile/</code> Firebase config and{' '}
              <code>public/install-links.json</code>; run EAS build for iOS/Android and publish store links.
            </li>
            <li>
              <strong>Ring / gate</strong> — Configure Ring OAuth redirect URIs and mailbox device IDs via{' '}
              Admin Mailbox. Register gate LPR webhook URL with camera vendor; seed <code>gate_plates</code>{' '}
              for pilot lots.
            </li>
            <li>
              <strong>Stripe</strong> — Create Stripe account, add publishable key to web env and{' '}
              <code>STRIPE_SECRET</code> to Functions. Test SetupIntent + invoice pay flow in staging before
              enabling live mode.
            </li>
            <li>
              <strong>Parcel GIS</strong> — Set <code>PARCEL_GIS</code> in <code>branding.config.js</code> for the
              customer county. Admin runs <strong>Sync all parcels from assessor</strong> from the Admin Portal after
              owner roster import.
            </li>
            <li>
              <strong>Marketing copy</strong> — Update <code>src/hoaProductCatalog.js</code> (pricing, add-ons, wizard)
              and mirror changes in this guide, <code>HelpPage.js</code>, and <code>/for-communities</code> when
              shipping customer-facing features.
            </li>
          </ol>
          <p>
            Current production branding: <strong>{branding.communityName}</strong> (
            {BRANDING_BY_MODE.production.websiteUrl}).
          </p>
        </section>

        <section id="frontend" className="docs-section">
          <h2>Frontend Architecture</h2>
          <h3>Entry & routing</h3>
          <p>
            <code>src/App.js</code> mounts the router, global auth state, weather/events fetching for the dashboard,
            and wraps owner routes in <code>ProtectedRoute</code>. Public routes: <code>/</code>,{' '}
            <code>/neighborhood-map</code>, <code>/privacy</code>, <code>/rentals</code> (rentals still behind auth
            when accessed from owner menu).
          </p>
          <h3>Key modules</h3>
          <ul>
            <li><code>src/header.js</code> — hamburger nav; Help + Admin Portal links</li>
            <li><code>src/firebase.js</code> — app init, <code>nmrdb</code> Firestore, Functions region</li>
            <li><code>src/ProtectedRoute.js</code> — standalone component; App also defines inline ProtectedRoute</li>
            <li><code>src/WeatherAlertBanner.js</code> — NWS alert strip</li>
            <li><code>src/MailDeliverySummary.js</code> — dashboard mailbox widget</li>
            <li><code>src/mailboxUtils.js</code> — shared mailbox formatting</li>
            <li><code>src/hoaProductCatalog.js</code> — pricing tiers, add-ons, sales wizard (source of truth for marketing)</li>
            <li><code>src/HoaSalesPage.js</code> — <code>/for-communities</code> sales + configurator</li>
            <li><code>src/OwnersPage.js</code> + <code>src/ParcelBoundaryMap.js</code> — owner directory + assessor parcel maps</li>
          </ul>
          <h3>Page inventory</h3>
          <p>
            Each feature maps ~1:1 to a top-level component under <code>src/</code>. Admin pages are prefixed{' '}
            <code>Admin*</code>. Identification pages call callable functions and write gallery collections.
          </p>
        </section>

        <section id="firebase" className="docs-section">
          <h2>Firebase Configuration</h2>
          <p>
            Canonical values live in <code>src/branding.config.js</code> (<code>FIREBASE_HINTS</code>).
          </p>
          <pre className="docs-code-block">{`Project ID:     ${FIREBASE_HINTS.projectId}
Auth domain:    ${FIREBASE_HINTS.authDomain}
Firestore DB:   ${FIREBASE_HINTS.firestoreDatabaseId} (non-default — must pass databaseId in client SDK)
Functions:      ${FIREBASE_HINTS.functionsRegion}
Hosting target: main → ${FIREBASE_HINTS.hostingTargets.main.public}/
Storage bucket: ${FIREBASE_HINTS.storageBucket}`}</pre>
          <p>
            <code>firebase.json</code> wires hosting rewrites, dual function codebases (<code>functions</code> +{' '}
            <code>admin-claims</code>), Firestore rules/indexes for <code>nmrdb</code>, and storage rules.
          </p>
        </section>

        <section id="functions" className="docs-section">
          <h2>Cloud Functions</h2>
          <p>
            <code>functions/index.js</code> is the export barrel. Logic is split into modules:
          </p>
          <table className="docs-table">
            <thead>
              <tr>
                <th>Module</th>
                <th>Responsibility</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><code>helpers.js</code></td><td>DB handle, CORS, rate limits, <code>userOwnsLot</code></td></tr>
              <tr><td><code>apiHandlers.js</code></td><td>HTTP <code>api</code> — weather, seismic, roads, wells, Stripe webhooks</td></tr>
              <tr><td><code>gateAccess.js</code></td><td>Plates CRUD, <code>openGate</code>, LPR webhook, access log</td></tr>
              <tr><td><code>ringMailbox.js</code></td><td>Ring OAuth, device config, webhook, delivery history</td></tr>
              <tr><td><code>ringHelpers.js</code></td><td>Token storage, account linking, handoff nonces</td></tr>
              <tr><td><code>mailboxVision.js</code></td><td>Grok vision prompts for mail/vehicle detection</td></tr>
              <tr><td><code>vehicleLog.js</code></td><td>Sightings, grouping, gate assignment, demo seed</td></tr>
              <tr><td><code>parcelGis.js</code></td><td>County tax-assessor ArcGIS lookup, owner <code>parcelGis</code> sync, boundary GeoJSON</td></tr>
              <tr><td><code>hoaFinance.js</code></td><td>HOA bank accounts, vendor bills (AP), payments, audit log — admin callables only</td></tr>
              <tr><td><code>hoaCompliance.js</code></td><td>Legal checklist, meetings, elections, owner governance calendar</td></tr>
              <tr><td><code>hoaComplianceCatalog.js</code></td><td>NC-default obligation templates (meetings, elections, taxes, records)</td></tr>
              <tr><td><code>hoaOperations.js</code></td><td>Work orders, amenity booking, visitor pre-reg (gate sync), parking, classifieds, e-sign</td></tr>
              <tr><td><code>environmental.js</code></td><td>PurpleAir / AirNow dashboard, wildfire risk, admin sensor config</td></tr>
              <tr><td><code>smsAlerts.js</code></td><td>Twilio SMS broadcasts when secrets set; audit log fallback</td></tr>
              <tr><td><code>otherCallables.js</code></td><td>Species ID, shared files, plane images, polls, cache</td></tr>
              <tr><td><code>notifications.js</code></td><td>Firestore triggers → email via Gmail secrets</td></tr>
              <tr><td><code>localEvents.js</code></td><td>Curated WNC event generator</td></tr>
              <tr><td><code>flightData.js</code></td><td>ADS-B / FR24 proxy</td></tr>
              <tr><td><code>videoCopy.js</code></td><td>Scheduled webcam archive copy</td></tr>
            </tbody>
          </table>
          <h3>Schedulers</h3>
          <ul>
            <li><code>syncLocalEvents</code> — daily 06:00 UTC, populates <code>events</code> for 12 months</li>
            <li><code>cleanOldCache</code> — daily identification cache purge</li>
          </ul>
          <h3>Secrets (defineSecret)</h3>
          <p>
            GMAIL_USER, GMAIL_PASS, XAI_API_KEY, STRIPE_SECRET, RAPIDAPI_KEY, FR24_TOKEN, WEATHER_API_KEY — bound
            per-function where needed. SMS: TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_FROM_NUMBER. Optional environmental:
            PURPLEAIR_API_KEY, AIRNOW_API_KEY (see <code>environmental.js</code>, <code>smsAlerts.js</code>).
          </p>
        </section>

        <section id="firestore" className="docs-section">
          <h2>Firestore Data Model</h2>
          <p>All collections live in <code>nmrdb</code>. Primary collections:</p>
          <table className="docs-table">
            <thead>
              <tr>
                <th>Collection</th>
                <th>Purpose</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><code>owners</code></td><td>Lot records — OwnerId, tract, contacts; optional <code>parcelGis</code> (PIN, map links, acreage)</td></tr>
              <tr><td><code>user_preferences</code></td><td>Per-UID notification toggles</td></tr>
              <tr><td><code>invoices</code></td><td>HOA billing documents</td></tr>
              <tr><td><code>polls</code> / <code>polls/{'{id}'}/votes</code></td><td>Community polls; one vote doc per lotId</td></tr>
              <tr><td><code>message_board</code></td><td>Posts and threaded replies</td></tr>
              <tr><td><code>events</code></td><td>Calendar entries (Timestamp <code>start</code>)</td></tr>
              <tr><td><code>gate_plates</code></td><td>Registered plates per lot — plate, label, active</td></tr>
              <tr><td><code>gate_access_log</code></td><td>Append-only gate events</td></tr>
              <tr><td><code>mailbox_config</code></td><td>Community Ring config doc (device IDs, thresholds)</td></tr>
              <tr><td><code>mailbox_delivery</code></td><td>Daily delivery summary keyed by date</td></tr>
              <tr><td><code>mailbox_events</code></td><td>Raw Ring motion / detection events</td></tr>
              <tr><td><code>ring_tokens</code>, <code>ring_account_links</code>, <code>ring_unclaimed_tokens</code>, <code>ring_link_handoffs</code></td><td>Ring OAuth state machine</td></tr>
              <tr><td><code>vehicle_groups</code></td><td>Aggregated vehicle identity (plate, make, model, color)</td></tr>
              <tr><td><code>vehicle_sightings</code></td><td>Individual camera captures with vision metadata</td></tr>
              <tr><td><code>violations</code></td><td>HOA violation notices per lot — open/resolved status</td></tr>
              <tr><td><code>sms_alert_log</code></td><td>Admin SMS alert log — live Twilio delivery counts when configured</td></tr>
              <tr><td><code>hoa_work_orders</code></td><td>Owner maintenance requests — submitted → assigned → completed</td></tr>
              <tr><td><code>hoa_amenity_resources</code> / <code>hoa_amenity_reservations</code></td><td>Clubhouse/pool booking</td></tr>
              <tr><td><code>hoa_visitor_registrations</code></td><td>Guest pre-reg; syncs <code>owner_temp</code> gate plates</td></tr>
              <tr><td><code>hoa_parking_permits</code></td><td>Guest/annual permits with admin approval</td></tr>
              <tr><td><code>community_classifieds</code></td><td>Owner marketplace — admin moderation</td></tr>
              <tr><td><code>hoa_esign_documents</code> / <code>hoa_esign_signatures</code></td><td>Rule-change acknowledgments per lot</td></tr>
              <tr><td><code>environmental_config</code></td><td>PurpleAir sensor index, alert thresholds, map links</td></tr>
              <tr><td><code>flora</code>, <code>animals</code>, <code>minerals</code></td><td>Identification gallery entries</td></tr>
              <tr><td><code>shared_files</code></td><td>HOA document metadata</td></tr>
              <tr><td><code>hoa_bank_accounts</code></td><td>Manual operating/reserve balances — Plaid-ready <code>connectionType</code></td></tr>
              <tr><td><code>vendor_bills</code></td><td>AP invoices linked to <code>contractors</code> — draft → approved → paid</td></tr>
              <tr><td><code>vendor_payments</code></td><td>Recorded check/ACH/wire payments; decrements bank balance when linked</td></tr>
              <tr><td><code>finance_audit_log</code></td><td>Append-only admin finance actions</td></tr>
              <tr><td><code>hoa_compliance_config</code></td><td>State, fiscal year start, default meeting notice days</td></tr>
              <tr><td><code>hoa_compliance_obligations</code></td><td>FY legal checklist items with due dates and completion status</td></tr>
              <tr><td><code>hoa_meetings</code></td><td>Scheduled sessions with notice-sent tracking</td></tr>
              <tr><td><code>hoa_elections</code></td><td>Director elections — nominations, ballot method, certification</td></tr>
              <tr><td><code>compliance_audit_log</code></td><td>Append-only governance actions</td></tr>
              <tr><td><code>protected_videos</code></td><td>Webcam stream ACL metadata</td></tr>
              <tr><td><code>cache</code></td><td>API response cache with TTL</td></tr>
            </tbody>
          </table>
          <p>
            Security rules in <code>firestore.rules</code> enforce owner-scoped reads and admin writes on sensitive
            collections. Indexes in <code>firestore.indexes.json</code> support gate logs, vehicle queries, and polls.
          </p>
        </section>

        <section id="storage" className="docs-section">
          <h2>Cloud Storage</h2>
          <p>
            <code>storage.rules</code> gates uploads by auth and path prefix. Notable paths:
          </p>
          <ul>
            <li><code>community_pics/</code> — owner photo uploads</li>
            <li><code>vehicle_sightings/</code> — Ring snapshots; thumbnails use Firebase download tokens (<code>thumbnailToken</code>)</li>
            <li><code>shared_files/</code> — HOA documents</li>
            <li><code>identification/</code> — species ID source images</li>
          </ul>
          <p>
            Vehicle thumbnails are served via token URLs (<code>.../o/{'{path}'}?alt=media&token={'{token}'}</code>)
            rather than GCS signed URLs for browser compatibility.
          </p>
        </section>

        <section id="auth" className="docs-section">
          <h2>Authentication & Admin Claims</h2>
          <p>
            Owners authenticate with Firebase email/password. Lot ownership is verified server-side via{' '}
            <code>owners</code> collection (<code>OwnerId</code>, <code>OwnerId2</code>, <code>ownerId</code> fields).
          </p>
          <p>
            Admin privileges use the custom claim <code>isAdmin: true</code>, set by the separate{' '}
            <code>admin-claims</code> codebase. After claim changes, users must refresh their ID token (re-login or{' '}
            <code>getIdToken(true)</code>).
          </p>
          <p>
            Callable functions should verify <code>request.auth</code> and call shared helpers; HTTP endpoints verify
            Bearer tokens or webhook secrets.
          </p>
        </section>

        <section id="gate" className="docs-section">
          <h2>Gate Access System</h2>
          <ol>
            <li>Owner registers plate via <code>upsertGatePlate</code> → doc in <code>gate_plates</code>.</li>
            <li>Manual open: <code>openGate</code> validates ownership and triggers gate controller.</li>
            <li>LPR webhook <code>gateLprWebhook</code> receives camera reads, matches active plates, logs to <code>gate_access_log</code>.</li>
            <li>Ring gate-camera motion also logs <code>vehicle_sighting</code> events (and auto-open when assigned) via <code>vehicleLog.js</code>.</li>
          </ol>
          <p>
            Frontend: <code>src/GateAccessPage.js</code> (recent access auto-refreshes every 30s). Access log methods
            include <code>lpr</code>, <code>manual</code>, <code>vehicle_sighting</code>, <code>vehicle_plate</code>, and{' '}
            <code>vehicle_vision</code>. Gate-role Ring devices log vehicles even when not selected for mailbox delivery.
          </p>
        </section>

        <section id="mailbox" className="docs-section">
          <h2>Mailbox & Ring Integration</h2>
          <h3>Linking flow</h3>
          <ol>
            <li>Admin starts <code>initiateRingMailboxLink</code> → OAuth handoff nonce in <code>ring_link_handoffs</code>.</li>
            <li>Ring redirects back; <code>finishRingMailboxLink</code> stores refresh tokens in <code>ring_tokens</code>.</li>
            <li>Devices selected via <code>listRingMailboxDevices</code> / <code>updateMailboxConfig</code>.</li>
          </ol>
          <h3>Runtime</h3>
          <p>
            <code>ringMailboxWebhook</code> receives motion events → downloads candidate frames (
            <code>downloadRingDeviceImageCandidates</code> in ringHelpers) → vision analysis → updates{' '}
            <code>mailbox_delivery</code> and <code>mailbox_events</code>. Vehicle detections may also feed{' '}
            <code>vehicle_sightings</code>. Gate cameras assigned role <code>gate</code> in <code>deviceRoles</code> are
            processed for vehicle logging even if not in <code>deviceIds</code> (mailbox-only list).
          </p>
          <p>
            Owner UI: <code>MailboxPage.js</code>. Admin setup: <code>AdminMailboxPage.js</code> — assign each device
            role (<code>gate</code>, <code>mailbox</code>, or <code>general</code>).
          </p>
        </section>

        <section id="vehicles" className="docs-section">
          <h2>Vehicle Log Pipeline</h2>
          <p>Admin-only <code>VehicleLogPage.js</code> calls <code>getVehicleLog</code> (auto-refreshes every 30s). Backend pipeline (A–E):</p>
          <ul>
            <li><strong>A — Plate-first grouping</strong> — 4+ char plates auto-merge at 0.97 confidence</li>
            <li><strong>B — Context prompts</strong> — gate vs mailbox-specific vision prompts in <code>mailboxVision.js</code></li>
            <li><strong>C — Fingerprint aggregation</strong> — <code>aggregateVehicleFingerprint()</code> merges plate, color, make, model, features</li>
            <li><strong>D — Candidate prefilter</strong> — <code>prefilterVehicleMatchCandidates()</code> + reference thumbnails in vision match</li>
            <li><strong>E — Multi-frame capture</strong> — sharpness-scored Ring frame selection</li>
          </ul>
          <p>
            Callables: <code>reassignVehicleSighting</code>, <code>assignVehicleGateAccess</code>,{' '}
            <code>unassignVehicleGateAccess</code>, <code>seedVehicleDemo</code> (demo data for QA).
          </p>
        </section>

        <section id="identification" className="docs-section">
          <h2>Identification APIs</h2>
          <p>
            <code>apiIdentifyPlant</code>, <code>apiIdentifyAnimal</code>, <code>apiIdentifyMineral</code> accept
            base64 images, call xAI with structured prompts, and persist results. <code>reclassifyAllIdentifications</code>{' '}
            batch-reprocesses galleries. <code>cleanOldCache</code> purges stale <code>cache</code> docs.
          </p>
        </section>

        <section id="notifications" className="docs-section">
          <h2>Notifications</h2>
          <p>
            Firestore triggers in <code>notifications.js</code> send email when:
          </p>
          <ul>
            <li>Site content updates (<code>notifyOnSiteUpdate</code>)</li>
            <li>Message board replies (<code>notifyOnMessageReply</code>)</li>
            <li>Shared file uploads (<code>notifyOnFileUpload</code>)</li>
            <li>Emergency message published (<code>notifyOnEmergencyMessage</code>)</li>
          </ul>
          <p>Recipients filtered by <code>user_preferences</code> notification flags.</p>
        </section>

        <section id="payments" className="docs-section">
          <h2>Stripe Payments</h2>
          <p>
            Invoice payment flow: <code>createSetupIntent</code> → save card → <code>payInvoiceWithPaymentMethod</code>.
            Payment methods managed via <code>getPaymentMethods</code>, <code>setDefaultPaymentMethod</code>,{' '}
            <code>detachPaymentMethod</code>. Stripe secret never exposed to client; publishable key in frontend env.
          </p>
        </section>

        <section id="hoa-finance" className="docs-section">
          <h2>HOA Finance (AP)</h2>
          <p>
            Owner accounts receivable stays on Stripe (<code>/admin-invoices</code>). HOA operating cash and vendor
            accounts payable live in <code>functions/hoaFinance.js</code> and <code>src/AdminFinancePage.js</code>{' '}
            (<Link to="/admin-finance" className="docs-link">/admin-finance</Link>).
          </p>
          <p>Admin callables (all require <code>isAdmin</code> custom claim):</p>
          <ul>
            <li><code>getFinanceDashboard</code> — cash on hand, AP outstanding, recent payments</li>
            <li><code>listBankAccounts</code> / <code>upsertBankAccount</code> / <code>recordBankBalance</code></li>
            <li><code>listVendorBills</code> / <code>upsertVendorBill</code> / <code>updateVendorBillStatus</code></li>
            <li><code>recordVendorPayment</code> / <code>listVendorPayments</code></li>
          </ul>
          <p>
            Bills reference <code>contractors</code> by ID. Status workflow: draft → pending_approval → approved → paid
            (or void). Recording a payment writes <code>vendor_payments</code>, marks the bill paid, and optionally
            decrements the selected bank account balance. Finance collections are server-only in <code>firestore.rules</code>.
          </p>
          <p>
            Roadmap add-ons in <code>hoaProductCatalog.js</code>: <code>bank-linking</code> (Plaid) and{' '}
            <code>accounting-sync</code> (QuickBooks/Xero).
          </p>
        </section>

        <section id="hoa-compliance" className="docs-section">
          <h2>Governance & Compliance</h2>
          <p>
            Helps boards track meetings, elections, and recurring legal obligations. Admin UI:{' '}
            <Link to="/admin-compliance" className="docs-link">/admin-compliance</Link>. Owner calendar:{' '}
            <Link to="/compliance-calendar" className="docs-link">/compliance-calendar</Link>.
          </p>
          <ul>
            <li><code>generateComplianceObligations</code> — seed FY checklist from <code>hoaComplianceCatalog.js</code></li>
            <li><code>upsertMeeting</code> / <code>recordMeetingHeld</code> — notice compliance vs. <code>noticeDaysRequired</code></li>
            <li><code>upsertElection</code> / <code>certifyElection</code> — optional link to <code>polls</code> for online ballots</li>
            <li><code>getPublicComplianceCalendar</code> — authenticated owners see open meetings and active elections</li>
          </ul>
          <p>All compliance collections are server-only in Firestore rules; includes disclaimer (not legal advice).</p>
        </section>

        <section id="hoa-operations" className="docs-section">
          <h2>Community Operations</h2>
          <p>
            Owner hub: <Link to="/community-services" className="docs-link">/community-services</Link>. Callable-only
            writes via <code>functions/hoaOperations.js</code>.
          </p>
          <ul>
            <li>Work orders — owners submit; admins assign contractors and close out</li>
            <li>Amenity reservations — admin defines resources; owners book time slots</li>
            <li>Visitor pre-registration — creates <code>owner_temp</code> gate plates automatically</li>
            <li>Parking permits — owner requests; admin approves with optional gate linkage</li>
            <li>Classifieds — moderated marketplace</li>
            <li>E-sign — admins publish documents; owners acknowledge per lot</li>
          </ul>
          <p>Admin SMS: <Link to="/admin-sms" className="docs-link">/admin-sms</Link> via <code>submitSmsAlert</code>.</p>
        </section>

        <section id="environmental" className="docs-section">
          <h2>Wildfire & Air Quality</h2>
          <p>
            Owner dashboard: <Link to="/environmental" className="docs-link">/environmental</Link>. Reads PurpleAir when
            <code>purpleAirSensorIndex</code> is set in <code>environmental_config</code>; falls back to AirNow by lat/lon.
          </p>
        </section>

        <section id="mobile" className="docs-section">
          <h2>Mobile App</h2>
          <p>
            Expo app under <code>mobile/</code> mirrors web routes in <code>mobile/app/</code>. Drawer navigation uses{' '}
            <code>mobile/constants/routes.ts</code>. Firebase callables shared via <code>mobile/services/functions.ts</code>.
            Install links synced to <code>public/install-links.json</code>.
          </p>
          <p>
            <strong>Sales note:</strong> Native iOS/Android apps are an optional add-on (+$5,500 setup · +$135/mo) — not
            bundled in base tiers. Documented in <code>hoaProductCatalog.js</code> and <code>/for-communities</code>.
          </p>
        </section>

        <section id="marketing" className="docs-section">
          <h2>Marketing & Product Catalog</h2>
          <p>
            <strong>Keep docs and marketing in sync.</strong> When you ship a customer-facing feature, update{' '}
            <code>src/hoaProductCatalog.js</code> first (pricing, <code>ADDON_MODULES</code>,{' '}
            <code>FEATURE_CATEGORIES</code>, <code>WIZARD_QUESTIONS</code>), then mirror summaries in:
          </p>
          <ul>
            <li><code>src/HoaSalesPage.js</code> — <Link to="/for-communities" className="docs-link">/for-communities</Link> sales page + configurator</li>
            <li><code>src/MarketingLandingPage.js</code> — public landing feature grid</li>
            <li><code>src/HoaBoardOnePager.js</code> — printable board one-pager (<code>BOARD_ONE_PAGER</code>)</li>
            <li><code>src/HelpPage.js</code> and <code>src/DeveloperGuidePage.js</code> (this page)</li>
            <li><code>functions/nannyKnowledge.js</code> — Nanny route summaries</li>
            <li><code>mobile/screens/HelpScreen.tsx</code> — mobile owner help</li>
          </ul>
          <p>Key exports in the catalog:</p>
          <ul>
            <li><code>PRICING_TIERS</code>, <code>ADDON_MODULES</code>, <code>MARKET_COMPARISON</code></li>
            <li><code>AI_MANAGEMENT_PILLARS</code>, <code>PMC_REPLACEMENT_CHECKLIST</code></li>
            <li><code>SMART_HOME_INTEGRATIONS</code> — clubhouse/pool/HVAC/lighting add-on marketing</li>
            <li><code>recommendTier()</code>, <code>suggestAddons()</code>, <code>buildRequirementsBrief()</code></li>
          </ul>
          <p>
            Lead capture: callable <code>submitHoaLead</code> stores configurator output. Demo site uses{' '}
            <code>npm run build:demo</code>; production sales CTAs point to <code>{BRANDING_BY_MODE.production.websiteUrl}/for-communities</code>.
          </p>
        </section>

        <section id="parcel-gis" className="docs-section">
          <h2>Parcel GIS (Tax Assessor)</h2>
          <p>
            NMR uses <strong>Macon County NC</strong> tax GIS (<code>gis.maconnc.org</code> ArcGIS REST). Production
            config: <code>PARCEL_GIS</code> in <code>branding.config.js</code>; client helpers in{' '}
            <code>src/parcelGis.js</code>; Functions in <code>functions/parcelGis.js</code>.
          </p>
          <ul>
            <li><code>lookupOwnerParcel</code> — admin: match owner address → PIN, acreage, map URLs; writes <code>owners.parcelGis</code></li>
            <li><code>syncAllOwnerParcels</code> — admin batch sync</li>
            <li><code>getParcelBoundary</code> — authenticated GeoJSON boundary for <code>ParcelBoundaryMap.js</code></li>
          </ul>
          <p>
            UI: <code>OwnersPage.js</code> — county map links, <strong>View lines</strong> modal, admin sync buttons.
            Firestore: owners collection readable by all authenticated users; writes admin-only.
          </p>
        </section>

        <section id="smart-integrations" className="docs-section">
          <h2>Smart-Home Integrations</h2>
          <p>
            Marketing and scoping live in <code>SMART_HOME_INTEGRATIONS</code> and related{' '}
            <code>ADDON_MODULES</code> entries (amenity locks, pool/spa, HVAC, lighting, custom sensors). These are{' '}
            <strong>custom add-ons</strong> — not implemented as a single module yet; each engagement is scoped after
            a site survey.
          </p>
          <p>Typical integrations boards request:</p>
          <ul>
            <li>Clubhouse / pool smart locks tied to amenity booking</li>
            <li>Pool pump & heater schedules, freeze alerts</li>
            <li>Common-area Nest/Ecobee HVAC monitoring</li>
            <li>Shelly/Lutron site lighting scenes</li>
            <li>Home Assistant / MQTT sensor feeds (extends gate & environmental monitoring)</li>
          </ul>
          <p>
            Configurator suggests matching add-ons when priorities include <strong>Clubhouse / pool / amenities</strong>{' '}
            or integrations list smart-home hardware. Update catalog pricing when quoting new installs.
          </p>
        </section>

        <section id="deploy" className="docs-section">
          <h2>Deployment & Operations</h2>
          <pre className="docs-code-block">{`# Web build + hosting
npm run build
firebase deploy --only hosting:main

# Functions (lint predeploy)
firebase deploy --only functions

# Rules / indexes
firebase deploy --only firestore:nmrdb,storage

# Selective function deploy
firebase deploy --only functions:getVehicleLog,functions:ringMailboxWebhook`}</pre>
          <p>
            Admin news refresh: callable/onRequest <code>searchLocalNews</code> (also triggered from Admin Portal).
            Daily events: <code>syncLocalEvents</code> scheduler — manual test via Functions logs.
          </p>
          <h3>Scripts</h3>
          <ul>
            <li><code>functions/scripts/seed-vehicle-demo.js</code> — demo vehicle log QA data</li>
            <li><code>functions/scripts/backfill-vehicle-thumbnail-tokens.js</code> — thumbnail token migration</li>
          </ul>
          <p>
            After marketing or owner-help changes: <code>npm run build && firebase deploy --only hosting:main</code>.
            Rules/indexes: <code>firebase deploy --only firestore:nmrdb</code>.
          </p>
        </section>

        <section id="local" className="docs-section">
          <h2>Local Development</h2>
          <pre className="docs-code-block">{`npm start                    # CRA dev server
firebase emulators:start     # Optional: functions + firestore
# functions/.env or Secret Manager for API keys in production`}</pre>
          <p>
            <code>src/firebase.js</code> connects to the Functions emulator when <code>NODE_ENV === 'development'</code>.
            Firestore emulator must use database ID <code>nmrdb</code> if testing named DB features.
          </p>
          <p>
            Set gcloud project: <code>gcloud config set project nantahala-mountain-resor-596d9</code> before running
            admin scripts against production Firestore.
          </p>
        </section>

        <footer className="docs-footer">
          <p>
            <Link to="/admin" className="docs-link">← Admin Portal</Link>
            {' · '}
            <Link to="/help" className="docs-link">Owner Help</Link>
          </p>
        </footer>
      </article>
    </div>
  );
}

export default DeveloperGuidePage;